import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type MouseEvent } from 'react';
import { getBootstrapData, TablePagination } from '@tpct/admin-ui';
import { addIconSm } from '@tpct/icons';
import FilePreviewContent from '@tpct/mission-core/components/FilePreviewContent';
import Modal from '@tpct/mission-core/components/Modal';
import ConnectorFileBrowser from '@tpct/mission-core/fileStore/ConnectorFileBrowser';
import FileLabellingTable, {
  createConnectorLabellingRows,
  createLabellingRows,
  type AnyLabellingRow,
} from '@tpct/mission-core/fileStore/FileLabellingTable';
import FileStoreTable, { type FileStoreView } from '@tpct/mission-core/fileStore/FileStoreTable';
import { createFileStoreApi, depositAll, type ConnectorPick, type KeptLabels } from '@tpct/mission-core/fileStore/fileStoreApi';
import KeptLabelsNotice from '@tpct/mission-core/fileStore/KeptLabelsNotice';
import type { LabelOptions, StoredFile } from '@tpct/mission-core/fileStore/fileStoreTypes';
import {
  useFileStore,
  type FileStoreFilterKey,
  type FileStoreFilterValue,
} from '@tpct/mission-core/fileStore/useFileStore';
import { useFileConnectors } from '@tpct/mission-core/fileStore/useFileConnectors';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import { getFecDsnImportGateFromBootstrap } from '@/hub-shared/gates/fecDsnImportGate';
import { __ } from '@/hub-shared/utils/i18n';
import FecDsnUploadResponsibilityNotice from '../../../../companies/src/shared/FecDsnUploadResponsibilityNotice';
import CompanyFileDeleteDialog from './CompanyFileDeleteDialog';
import CompanyFileEditDialog from './CompanyFileEditDialog';
import {
  isFilialeFile,
  isReadableIntoProfile,
  useProfileReadAndMerge,
  type MergeState,
} from './useProfileReadAndMerge';

type CompanyFilesTabProps = {
  companyId: number;
  bootstrapKey: string;
};

type FilesTabBootstrap = {
  restUrl: string;
  nonce: string;
  restUrlVsme?: string;
  isFecDsnImportEnabled?: boolean;
};

const NO_STRINGS: string[] = [];
const NO_YEARS: number[] = [];
const EMPTY_LABELS: LabelOptions = { types: [], entities: [] };
const HEADQUARTERS_KEY = 'siege';
/** Artifact statuses a user may read again: never read, or read and failed. */
const REREADABLE_STATUSES: ReadonlySet<string> = new Set(['uploaded', 'failed']);

const readBootstrap = (bootstrapKey: string): FilesTabBootstrap => {
  const data: FilesTabBootstrap = getBootstrapData(bootstrapKey);
  return data;
};

type FilterOption = { key: string; label: string; checked: boolean; onToggle: () => void };

type FilterMenuKey = 'type' | 'year' | 'entity';

type FilterMenuProps = {
  label: string;
  groupLabel: string;
  options: FilterOption[];
  open: boolean;
  onToggle: () => void;
};

/** Open state is owned by the toolbar so only one panel shows at a time (#994). */
const FilterMenu = ({ label, groupLabel, options, open, onToggle }: FilterMenuProps) => (
  <details className="company-files-tab__filter" open={open}>
    <summary
      onClick={(event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        onToggle();
      }}
    >
      {label}
    </summary>
    <fieldset aria-label={groupLabel}>
      {options.length === 0
        ? <p className="company-files-tab__filter-empty">{__('Aucune valeur')}</p>
        : options.map((option) => (
          <label key={option.key}>
            <input type="checkbox" checked={option.checked} onChange={option.onToggle} />
            {option.label}
          </label>
        ))}
    </fieldset>
  </details>
);

/** Values the answer offers, plus any already selected (the facets may have narrowed). */
const withSelected = <T,>(offered: readonly T[], selected: readonly T[]): T[] => [
  ...offered,
  ...selected.filter((value) => !offered.includes(value)),
];

const MergeResultNotice = ({ merge }: { merge: MergeState }) => {
  if (merge.kind === 'info') {
    return <p className="notice notice-info" aria-live="polite">{merge.message}</p>;
  }
  if (merge.kind !== 'done') {
    return null;
  }
  if (merge.result.prefill_unavailable === true) {
    return (
      <p className="notice notice-warning" role="alert">
        {__('Préremplissage indisponible : les données du profil entreprise n’ont pas pu être lues. Rien n’a été modifié dans le rapport.')}
      </p>
    );
  }
  const merged = merge.result.merged_field_ids?.length ?? 0;
  const kept = merge.result.skipped_non_empty ?? 0;
  return (
    <p className={`notice ${merged > 0 ? 'notice-success' : 'notice-info'}`} aria-live="polite">
      {`${merged} ${__('champ(s) prérempli(s)')}, ${kept} ${__('champ(s) déjà renseigné(s) conservé(s)')}`}
    </p>
  );
};

/**
 * "Fichiers" tab of a company dossier (file store live only): every stored file of the dossier,
 * filtered by type, year and entity, previewed, relabelled, deposited and deleted. What the user
 * may do comes from the list answer's `rights`, never from a bootstrap (ledger 40). Files arrive
 * from the computer or, when a connector can supply them, from that connector: both land in the
 * same labelling table and the same store, and a stored FEC/DSN is read into the profile and
 * offered to the draft report like the legacy import.
 */
const CompanyFilesTab = ({ companyId, bootstrapKey }: CompanyFilesTabProps) => {
  const bootstrap = useMemo(() => readBootstrap(bootstrapKey), [bootstrapKey]);
  const importEnabled = bootstrap.isFecDsnImportEnabled === true;
  const importGate = useMemo(() => getFecDsnImportGateFromBootstrap(bootstrap), [bootstrap]);
  const api = useMemo(
    () => createFileStoreApi({ apiBase: bootstrap.restUrl, nonce: bootstrap.nonce }),
    [bootstrap.restUrl, bootstrap.nonce],
  );
  const {
    list,
    filters,
    toggleFilter,
    search,
    setSearch,
    entityLabels,
    page,
    pageCount,
    setPage,
    refresh,
  } = useFileStore({
    api,
    ownerRecordId: companyId,
    wantedTypes: NO_STRINGS,
    suggestYears: NO_YEARS,
  });
  const {
    readDeposited,
    readingIds,
    readNotice,
    readErrors,
    merge,
    confirmMerge,
    declineMerge,
  } = useProfileReadAndMerge({
    api,
    companyId,
    bootstrapKey,
    vsmeBase: bootstrap.restUrlVsme,
    importEnabled,
    onRead: refresh,
  });

  const [view, setView] = useState<FileStoreView>('list');
  const [previewFile, setPreviewFile] = useState<StoredFile | null>(null);
  const [deleteFile, setDeleteFile] = useState<StoredFile | null>(null);
  const [editFile, setEditFile] = useState<StoredFile | null>(null);
  const [depositRows, setDepositRows] = useState<AnyLabellingRow[]>([]);
  const [browsingConnectors, setBrowsingConnectors] = useState(false);
  const [depositPhase, setDepositPhase] = useState<'idle' | 'depositing'>('idle');
  const [kept, setKept] = useState<KeptLabels[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openFilter, setOpenFilter] = useState<FilterMenuKey | null>(null);
  const filtersRef = useRef<HTMLDivElement>(null);

  const toggleFilterMenu = useCallback((key: FilterMenuKey) => {
    setOpenFilter((current) => (current === key ? null : key));
  }, []);

  // An open filter panel overlays the list: a press outside the filters or Escape dismisses it.
  useEffect(() => {
    if (openFilter === null) {
      return undefined;
    }
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (event.target instanceof Node && filtersRef.current?.contains(event.target) !== true) {
        setOpenFilter(null);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenFilter(null);
      }
    };
    document.addEventListener('pointerdown', closeOnOutsidePress);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePress);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [openFilter]);

  const data = list.data;
  const files = useMemo(() => (data === null ? [] : [...data.suggestions, ...data.files]), [data]);
  const labels = data?.labels ?? EMPTY_LABELS;
  const canDeposit = data?.rights.can_deposit === true && data.labels !== undefined;
  const canDelete = data?.rights.can_delete === true;
  const connectors = useFileConnectors(api, companyId, canDeposit);
  const canImportViaApi = canDeposit && connectors.length > 0;
  // One deposit or read at a time: a second one would race the first's merge offer.
  const busy = depositPhase === 'depositing' || readingIds.size > 0;

  const typeLabels = useMemo(() => {
    const byKey = new Map<string, string>(labels.types.map((type) => [type.key, type.label]));
    files.forEach((file) => {
      if (!byKey.has(file.type_key)) {
        byKey.set(file.type_key, file.type_label);
      }
    });
    return byKey;
  }, [labels.types, files]);

  const handlePreview = useCallback((file: StoredFile) => setPreviewFile(file), []);
  const handleDelete = useCallback((file: StoredFile) => setDeleteFile(file), []);
  const handleEdit = useCallback((file: StoredFile) => setEditFile(file), []);
  const handleReread = useCallback((file: StoredFile) => {
    void readDeposited([file]);
  }, [readDeposited]);
  const canReread = useCallback(
    (file: StoredFile) => !busy && isReadableIntoProfile(file) && REREADABLE_STATUSES.has(file.artifact.status),
    [busy],
  );
  const rowNote = useCallback(
    (file: StoredFile) => (file.artifact !== null && isFilialeFile(file)
      ? __('Fichier de filiale : non lu dans le profil du dossier.')
      : null),
    [],
  );
  const downloadPreview = useCallback((fileId: number) => api.downloadBlob(companyId, fileId, true), [api, companyId]);

  const handleFilesChosen = (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (chosen.length > 0) {
      setDepositRows(createLabellingRows(chosen));
    }
  };

  const handleConnectorPicked = (picks: ConnectorPick[]) => {
    setBrowsingConnectors(false);
    if (picks.length > 0) {
      setDepositRows(createConnectorLabellingRows(picks));
    }
  };

  const handleDeposit = async () => {
    setDepositPhase('depositing');
    const result = await depositAll(api, companyId, depositRows);
    setDepositPhase('idle');
    // Failed rows stay open with their error; stored ones leave the labelling table.
    setDepositRows(result.failed.map(({ row, error }) => ({ ...row, error: error.message })));
    setKept(result.kept);
    if (result.ok.length === 0) {
      return;
    }
    refresh();
    await readDeposited(result.ok.map((deposit) => deposit.file));
  };

  const handleKeptResolved = useCallback((fileId: number, applied: boolean) => {
    setKept((current) => current.filter((item) => item.file.id !== fileId));
    if (applied) {
      refresh();
    }
  }, [refresh]);

  const filterOptions = <K extends FileStoreFilterKey>(
    key: K,
    values: FileStoreFilterValue<K>[],
    labelOf: (value: FileStoreFilterValue<K>) => string,
  ): FilterOption[] => {
    const selected = filters[key] as FileStoreFilterValue<K>[];
    return withSelected(values, selected).map((value) => ({
      key: value === null ? HEADQUARTERS_KEY : String(value),
      label: labelOf(value),
      checked: selected.includes(value),
      onToggle: () => toggleFilter(key, value),
    }));
  };

  const typeOptions = filterOptions('type', data?.facets.types ?? [], (key) => typeLabels.get(key) ?? key);
  const yearOptions = filterOptions('year', [...(data?.facets.years ?? [])].sort((a, b) => b - a), String);
  const entityOptions = filterOptions(
    'entity',
    (data?.facets.entities ?? []).map((entity) => entity.id),
    (id) => entityLabels.get(id) ?? (id === null ? __('Siège social') : String(id)),
  );

  return (
    <section className="company-files-tab">
      <div className="company-files-tab__header">
        {canDeposit && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="visually-hidden"
              aria-label={__('Choisir des fichiers à déposer')}
              disabled={busy}
              onChange={handleFilesChosen}
            />
            {canImportViaApi && (
              <button
                type="button"
                className="btn-secondary company-files-tab__import"
                disabled={busy}
                onClick={() => setBrowsingConnectors(true)}
              >
                <img src={addIconSm} alt="" aria-hidden="true" className="company-files-tab__import-icon" />
                {__('Importer via API')}
              </button>
            )}
            <button
              type="button"
              className="btn-secondary company-files-tab__import"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              <img src={addIconSm} alt="" aria-hidden="true" className="company-files-tab__import-icon" />
              {__("Importer via l'ordinateur")}
            </button>
          </>
        )}
      </div>

      {readErrors.length > 0 && (
        <div className="notice notice-error" role="alert">
          <ul>
            {readErrors.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
      )}
      {readNotice !== null && (
        <p className={`notice ${readNotice.partial ? 'notice-warning' : 'notice-success'}`} aria-live="polite">
          {readNotice.text}
        </p>
      )}
      <MergeResultNotice merge={merge} />
      <KeptLabelsNotice
        api={api}
        ownerRecordId={companyId}
        items={kept}
        labelOptions={labels}
        onResolved={handleKeptResolved}
      />

      <div className="company-files-tab__toolbar">
        <input
          type="search"
          className="company-files-tab__search"
          aria-label={__('Rechercher')}
          placeholder={__('Rechercher')}
          value={search}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
        />
        <div className="company-files-tab__filters" ref={filtersRef}>
          <span>{__('Filtrer par :')}</span>
          <FilterMenu
            label={__('Type')}
            groupLabel={__('Filtrer par type')}
            options={typeOptions}
            open={openFilter === 'type'}
            onToggle={() => toggleFilterMenu('type')}
          />
          <FilterMenu
            label={__('Année')}
            groupLabel={__('Filtrer par année')}
            options={yearOptions}
            open={openFilter === 'year'}
            onToggle={() => toggleFilterMenu('year')}
          />
          <FilterMenu
            label={__('Entité')}
            groupLabel={__('Filtrer par entité')}
            options={entityOptions}
            open={openFilter === 'entity'}
            onToggle={() => toggleFilterMenu('entity')}
          />
        </div>
      </div>

      {list.status === 'error' && (
        <div className="notice notice-error" role="alert">
          <p>{list.error}</p>
          <button type="button" className="btn-secondary" onClick={refresh}>{__('Réessayer')}</button>
        </div>
      )}
      {data === null && list.status === 'loading' && (
        <p className="company-files-tab__loading" role="status">{__('Chargement des fichiers…')}</p>
      )}
      {data !== null && (
        <div aria-busy={list.status === 'loading'}>
          <FileStoreTable
            files={files}
            view={view}
            onViewChange={setView}
            label={__('Fichiers du dossier')}
            canDelete={canDelete}
            onPreview={handlePreview}
            onDelete={handleDelete}
            onEdit={canDeposit ? handleEdit : undefined}
            statusOverrides={readingIds}
            onReread={importEnabled && canDeposit ? handleReread : undefined}
            canReread={canReread}
            rowNote={rowNote}
            emptyText={__('Aucun fichier ne correspond.')}
          />
          <TablePagination
            page={page - 1}
            totalPages={pageCount}
            onPrev={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
            previousPageAriaLabel={__('Page précédente')}
            nextPageAriaLabel={__('Page suivante')}
            hideWhenSinglePage
          />
        </div>
      )}

      {browsingConnectors && canImportViaApi && (
        <Modal
          isOpen
          onClose={() => setBrowsingConnectors(false)}
          title={__('Importer via API')}
          size="large"
        >
          <ConnectorFileBrowser
            apiBase={bootstrap.restUrl}
            nonce={bootstrap.nonce}
            ownerRecordId={companyId}
            connectors={connectors}
            onPicked={handleConnectorPicked}
            onCancel={() => setBrowsingConnectors(false)}
          />
        </Modal>
      )}

      {depositRows.length > 0 && (
        <Modal
          isOpen
          onClose={() => setDepositRows([])}
          title={__('Déposer des fichiers')}
          size="large"
          closeOnOverlayClick={depositPhase === 'idle'}
          closeOnEscape={depositPhase === 'idle'}
          showCloseButton={depositPhase === 'idle'}
        >
          <FeatureGateNotice decision={importGate} />
          {!importEnabled && (
            <p className="description">
              {__('Les fichiers FEC / DSN déposés sont enregistrés, mais pas encore lus dans le profil du dossier.')}
            </p>
          )}
          <FecDsnUploadResponsibilityNotice />
          <FileLabellingTable<AnyLabellingRow>
            rows={depositRows}
            onRowsChange={setDepositRows}
            labelOptions={labels}
            onSubmit={() => {
              void handleDeposit();
            }}
            submitting={depositPhase === 'depositing'}
            onCancel={() => setDepositRows([])}
          />
        </Modal>
      )}

      {previewFile !== null && (
        <Modal isOpen onClose={() => setPreviewFile(null)} title={previewFile.name} size="large">
          <FilePreviewContent
            isOpen
            file={{
              id: previewFile.id,
              file_name: previewFile.name,
              mime_type: previewFile.mime,
              download_url: api.downloadUrl(companyId, previewFile.id, false),
            }}
            downloadFile={downloadPreview}
          />
        </Modal>
      )}

      {deleteFile !== null && (
        <CompanyFileDeleteDialog
          api={api}
          ownerId={companyId}
          file={deleteFile}
          onClose={() => setDeleteFile(null)}
          onDeleted={() => {
            setDeleteFile(null);
            refresh();
          }}
        />
      )}

      {editFile !== null && (
        <CompanyFileEditDialog
          api={api}
          ownerId={companyId}
          file={editFile}
          types={labels.types}
          onClose={() => setEditFile(null)}
          onSaved={() => {
            setEditFile(null);
            refresh();
          }}
        />
      )}

      {merge.kind === 'confirm' && (
        <Modal
          isOpen
          onClose={declineMerge}
          title={__('De nouvelles données sont disponibles depuis l’import. Les appliquer au rapport VSME en cours ?')}
          size="small"
          closeOnOverlayClick={!merge.busy}
          closeOnEscape={!merge.busy}
          showCloseButton={!merge.busy}
        >
          {merge.error !== null && <p className="notice notice-error" role="alert">{merge.error}</p>}
          <div className="form-actions">
            <button
              type="button"
              className="btn-primary"
              disabled={merge.busy}
              onClick={() => {
                void confirmMerge();
              }}
            >
              {merge.busy ? __('Application…') : __('Oui')}
            </button>
            <button type="button" className="btn-secondary" disabled={merge.busy} onClick={declineMerge}>
              {__('Non')}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
};

export default CompanyFilesTab;
